import React, { FC } from 'react';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FamilyWithSurveyDto, SurveyAnswerDto, SurveyStatusEnum } from '@families-link/shared';
import { FamilyDetails } from '../../../family/components/family-details/family-details.component';
import { SurveyResultsAnswerList } from './survey-answers-list.component';
import { CreateSurvey } from './survey-create.component';
import { SendSurvey } from './survey-send.component';
import { ManualEditingSurvey } from './survey-manual-editing.component copy';
import { useGetFamilyWithSurveyQuery } from '../../../../store/api/survey.api';
import { CoreLoadingWall } from '../../../_core/components/_ui/core-loading-wall';
import { UnrecognizedFamilyDetails } from '../../../family/components/family-details/unrecognized-family-details.component';
import { CoreButton } from '../../../_core/components/_ui/core-button';

export type ResultsSurveyProps = {
	resultComponentStatus: 'recognized' | 'unrecognized';
	currentEngagementId?: string;
	currentFamilyWithSurvey?: FamilyWithSurveyDto;
	surveyAnswer?: SurveyAnswerDto;
	handleOpenModalCreate(): void;
	handleOnManualEditing(f?: FamilyWithSurveyDto): Promise<void>;
	handleSubmitSend(f?: FamilyWithSurveyDto): Promise<void>;
	closeResultsComponent(): void;
	handleDeleteSurveyAnswer(data?: SurveyAnswerDto): void;
	handleAttachSurveyAnswerToFamily(data?: SurveyAnswerDto): void;
};

export const ResultsSurvey: FC<ResultsSurveyProps> = (props) => {
	const {
		resultComponentStatus,
		surveyAnswer,
		currentFamilyWithSurvey,
		currentEngagementId,
		handleOpenModalCreate,
		handleSubmitSend,
		handleOnManualEditing,
		closeResultsComponent,
		handleDeleteSurveyAnswer,
		handleAttachSurveyAnswerToFamily,
	} = props;
	const [t] = useTranslation();

	const { data: updatedFamilyWithSurvey, isLoading } = useGetFamilyWithSurveyQuery(
		{ familyId: currentFamilyWithSurvey?.family._id, engagementId: currentEngagementId },
		{ skip: !currentFamilyWithSurvey?.family._id || !currentEngagementId }
	);

	if (isLoading) {
		return (
			<Container
				maxWidth={false}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100%',
					position: 'relative',
				}}
			>
				<CoreLoadingWall />
			</Container>
		);
	}

	return (
		<Box			
			sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0, position: 'relative' }}
		>
			<Box>
				<Box sx={{ display: 'flex', width: '100%', mb: 3 }}>
					<Button
						onClick={closeResultsComponent}
						sx={{
							height: '35px',
							width: '35px',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minWidth: 0,
							position: 'absolute',
							top: '4px',
							left: '0px',
							zIndex: 10,
							borderRadius: '50%',
							fontSize: '20px',
							color: (theme) => theme.colors.grayBlue,
							'&:hover': { backgroundColor: (theme) => theme.colors.grayLight },
						}}
					>{`<`}</Button>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 500, marginY: '8px', marginLeft: '40px', fontSize: '16px' }}>
							{resultComponentStatus === 'recognized' ? t('families.family-contact') : 'Unrecognized Answer Details'}
						</Typography>
					</Box>
					{resultComponentStatus === 'unrecognized' ? (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
							<CoreButton
								onClick={() => handleAttachSurveyAnswerToFamily(surveyAnswer)}
								variant="info-light"
								sx={{ maxWidth: '110px', paddingY: 1, paddingX: 2,height: '40px', }}
							>
								{
									<Typography
										sx={{
											display: 'block',
											overflow: 'hidden',
											whiteSpace: 'nowrap',
											textOverflow: 'ellipsis',
											color: 'inherit',
											fontSize: '14px',
											fontWeight: '500',
											
										}}
									>
										{'Assign to family'}
									</Typography>
								}
							</CoreButton>
							<CoreButton onClick={() => handleDeleteSurveyAnswer(surveyAnswer)} sx={{ paddingY: 1, paddingX: 2, fontSize: '14px' }}>
								Delete
							</CoreButton>{' '}
						</Box>
					) : null}
				</Box>
				{resultComponentStatus === 'recognized' ? (
					<FamilyDetails
						containerSx={{ '&.MuiContainer-root': { paddingX: 0 } }}
						familyId={currentFamilyWithSurvey?.family._id}
						isEngagementPage
					/>
				) : (
					<UnrecognizedFamilyDetails
						fullName={surveyAnswer?.fullName ?? ''}
						phoneNumber={surveyAnswer?.phoneNumber ?? ''}
					/>
				)}
			</Box>
			{resultComponentStatus === 'unrecognized' ? (
				<SurveyResultsAnswerList surveyAnswer={surveyAnswer} />
			) : updatedFamilyWithSurvey?.surveyStatus === SurveyStatusEnum.Completed ? (
				<SurveyResultsAnswerList surveyAnswer={updatedFamilyWithSurvey?.surveyAnswer} />
			) : updatedFamilyWithSurvey?.surveyStatus === SurveyStatusEnum.NoSurvey ? (
				<CreateSurvey handleOpenModalCreate={handleOpenModalCreate} />
			) : updatedFamilyWithSurvey?.surveyStatus === SurveyStatusEnum.NotShared ? (
				<SendSurvey
					surveyTemplateName={updatedFamilyWithSurvey?.surveyTemplate?.title}
					currentFamilyWithSurvey={updatedFamilyWithSurvey}
					handleSubmitSend={handleSubmitSend}
				/>
			) : (
				<ManualEditingSurvey
					currentFamilyWithSurvey={updatedFamilyWithSurvey}
					handleOnManualEditing={handleOnManualEditing}
				/>
			)}
		</Box>
	);
};
